<script>
import * as animationDataSuccess from '@/../public/assets/animations/9917-success.json'
import Lottie from '@/components/general/lottie.vue'
import baseMixin from '@/mixins/baseMixin'
import $dayjs from '@/services/date'
import BookingType from '@/shared/constants/booking/BookingType'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { mapGetters } from 'vuex'

export default {
  name: 'BookingCreateSummaryCard',
  components: {
    Lottie,
  },
  mixins: [baseMixin],
  props: {
    editMode: { type: Boolean, default: false },
    bookingMode: { type: String, default: null },
    summaryData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultOptions: {
        loop: false,
        autoplay: true,
        animationData: animationDataSuccess.default,
      },
      animationSpeed: 1,
      BookingType,
    }
  },
  computed: {
    ...mapGetters({
      getClassifications: 'client/classifications',
      getCerts: 'locations/certifications',
      getGrades: 'client/grades',
    }),
    bookingsSubtitle() {
      return this.$tc(
        this.editMode
          ? 'bookingEdit.summarySubheading'
          : 'bookingCreate.summarySubheading',
        this.summaryData.bookingsPerDate,
        {
          bookingsPerDate: this.summaryData.bookingsPerDate,
          numBookings: this.response.bookings.length,
        }
      )
    },
    getDistinctCertList() {
      const certs = this.getCerts(this.summaryData.selectedLocation.locationId)

      if (certs.length === 0) return []

      const distinctList = []

      // Filter out any duplicate cert requirements
      for (const cert of certs) {
        if (distinctList.find((c) => c.id === cert.id)) continue
        distinctList.push(cert)
      }

      return distinctList
    },
    payload() {
      return this.summaryData.payload
    },
    response() {
      return this.summaryData.response
    },
  },
  methods: {
    bookingsFromTemplate(templateId) {
      return this.response.bookings.filter((x) => x.templateId === templateId)
    },
    formatBookingDate(date) {
      return $dayjs(date)
        .locale(this.$i18n.locale.toLowerCase())
        .format(DateFormatToken.ddddL)
    },
    classification(templateId) {
      return this.getClassifications(
        this.summaryData.selectedLocation.clientId
      )?.find(
        (x) => x.id === this.getPayloadTemplateById(templateId)?.classification
      )?.onlineTitle
    },
    purchaseOrderNumber(templateId) {
      const purchaseOrderNumber = this.getPayloadTemplateById(
        templateId
      )?.purchaseOrderNumber?.trim()
      return purchaseOrderNumber || null
    },
    grades(templateId) {
      const grades = this.getGrades(this.summaryData.selectedLocation.clientId)
      const templateGrades = this.getPayloadTemplateById(templateId)?.grades
      if (!grades || !templateGrades) return []
      return grades.filter((x) => templateGrades.includes(x.id))
    },
    confirmations(templateId) {
      const templateConfirmations = this.getPayloadTemplateById(templateId)
        ?.confirmations
      if (!templateConfirmations) return []
      return templateConfirmations.map((x) => x.fullName)
    },
    certifications(templateId) {
      const certs = this.getDistinctCertList
      const templateCerts = this.getPayloadTemplateById(templateId)
        ?.certifications
      if (!certs || !templateCerts) return []
      return certs?.filter((x) => templateCerts?.includes(x.id))
    },
    getPayloadTemplateById(templateId) {
      // If edit booking for single day
      if (this.editMode && this.bookingMode === BookingType.SingleDay) {
        return this.payload.templates[0]
      }
      return this.payload.templates.find((x) => x.id === templateId)
    },
    handleAnimation: function(anim) {
      this.anim = anim
    },
  },
}
</script>

<template>
  <v-row class="justify-center">
    <v-col :cols="isMobileViewPort ? 12 : 5">
      <v-card class="rounded-xl custom-elevation">
        <v-card-title class="d-flex flex-column">
          <Lottie
            :options="defaultOptions"
            :height="130"
            :width="130"
            @animCreated="handleAnimation"
          />
          <h3 class="mt-4"> {{ $t('bookingCreate.summaryTitle') }} </h3>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-for="template of response.templates" :key="template.id">
          <section class="pt-4">
            <article>
              <header id="summary-booking-list-subtitle">
                {{ bookingsSubtitle }}
              </header>

              <v-list class="summary-booking-list">
                <v-list-item
                  v-for="booking in bookingsFromTemplate(template.id)"
                  :key="
                    `summary-booking-list-item-${booking.id}-${template.id}`
                  "
                  class="summary-booking-list-item"
                >
                  <v-list-item-content
                    class="font-monospace"
                    v-text="formatBookingDate(booking.startDateLocation)"
                  >
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-chip class="font-monospace" small>
                      {{ `#${booking.id}` }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </article>

            <v-divider />

            <article class="pt-4">
              <header>
                {{ $t('bookingCreate.summaryMoreInfoHeading') }}
              </header>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{ $t('bookingCreate.summaryBookingTimeslotHeading') }}
                    </v-list-item-subtitle>
                    <v-list-item-title class="font-monospace">{{
                      `${template.startTimeLocation} - ${template.endTimeLocation}`
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{ $t('bookingCreate.summaryClassificationsHeading') }}
                    </v-list-item-subtitle>
                    <v-list-item-title
                      class="summary-booking-list-item__classification"
                    >
                      {{ classification(template.id) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{ $t('bookingCreate.summaryGradesHeading') }}
                    </v-list-item-subtitle>
                    <div v-if="grades(template.id).length > 0">
                      <v-chip
                        v-for="grade in grades(template.id)"
                        :key="`grades-list-item-${grade.id}-${template.id}`"
                        small
                        class="summary-booking-list-item__grades mr-1 mb-1"
                      >
                        {{ grade.title }}
                      </v-chip>
                    </div>
                    <span v-else> {{ $t('general.none') }} </span>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="purchaseOrderNumber(template.id)">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{
                        $t('bookingCreate.summaryPurchaseOrderNumberHeading')
                      }}
                    </v-list-item-subtitle>
                    <v-list-item-title
                      class="summary-booking-list-purchase-order-number"
                    >
                      {{ purchaseOrderNumber(template.id) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{ $t('bookingCreate.summaryConfirmationsHeading') }}
                    </v-list-item-subtitle>
                    <div v-if="confirmations(template.id).length > 0">
                      <v-chip
                        v-for="confirmation in confirmations(template.id)"
                        :key="
                          `confirmations-list-item-${confirmation}-${template.id}`
                        "
                        small
                        class="summary-booking-list-item__confirmation mr-1 mb-1"
                      >
                        {{ confirmation }}
                      </v-chip>
                    </div>
                    <span v-else> {{ $t('general.none') }} </span>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{ $t('bookingCreate.summaryCertificationsHeading') }}
                    </v-list-item-subtitle>
                    <div v-if="certifications(template.id).length > 0">
                      <v-chip
                        v-for="certification in certifications(template.id)"
                        :key="
                          `certifications-list-item-${certification.id}-${template.id}`
                        "
                        small
                        class="summary-booking-list-item__certification mr-1 mb-1"
                      >
                        {{ certification.name }}
                      </v-chip>
                    </div>
                    <span v-else> {{ $t('general.none') }} </span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </article>
          </section>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            id="booking-create-summary__return-btn"
            :to="{ name: 'home' }"
            exact
            text
          >
            <v-icon left>mdi-home</v-icon>
            {{ $t('bookingCreate.summaryReturnHomeBtn') }}
          </v-btn>
          <v-btn
            v-if="!editMode"
            id="booking-create-summary__create-btn"
            color="blue"
            text
            @click="$emit('createNewBooking')"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t('bookingCreate.summaryCreateAnotherBtn') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>
